import { useEffect } from 'react';

import type { PartnershipReferralResponse } from 'growth/usePartnershipReferrals';
import { useObservability } from 'platform/observability';
import type { SellerTransaction } from 'sellDirect/graphql/useSellerTransaction';

type TransactionWithPartnershipReferrals = Extract<
  SellerTransaction,
  { __typename: 'QualifiedTransaction' | 'InContractTransaction' }
>;

const isTransactionWithPartnershipReferrals = (
  sellerTransaction: SellerTransaction,
): sellerTransaction is TransactionWithPartnershipReferrals =>
  sellerTransaction.__typename === 'QualifiedTransaction' ||
  sellerTransaction.__typename === 'InContractTransaction';

type Params = {
  sellerTransaction: SellerTransaction | null;
  sellerTransactionLoading: boolean;
  partnershipReferralData: PartnershipReferralResponse;
  /** Computed in QualificationContext: partnershipReferralData.blockBuyerContent || isAgentChannel */
  blockBuyerContent: boolean;
  /** Computed in QualificationContext: (hasAgentReferral && !isTrueAgentReferral) || lead?.agentExperience */
  shouldSeeAgentExperience: boolean;
  sellerLeadUuid: string | undefined;
};

/**
 * Shadow-compares partnership referral flags from sellerTransaction.partnershipReferrals
 * against the legacy usePartnershipReferrals source
 */
export const useShadowComparePartnershipReferrals = ({
  sellerTransaction,
  sellerTransactionLoading,
  partnershipReferralData,
  blockBuyerContent,
  shouldSeeAgentExperience,
  sellerLeadUuid,
}: Params): void => {
  const { trackError } = useObservability();

  useEffect(() => {
    if (sellerTransactionLoading || partnershipReferralData.loading) return;
    if (!sellerTransaction || !isTransactionWithPartnershipReferrals(sellerTransaction)) return;

    const txReferrals = sellerTransaction.partnershipReferrals;

    // If the transaction has no referral snapshot but the legacy source reports one,
    // or vice versa, flag it and stop, per-field comparisons would just be noise.
    const legacyIsPartnershipLead = partnershipReferralData.isPartnershipLead;
    if (!txReferrals) {
      if (legacyIsPartnershipLead) {
        trackError('partnership-referrals-shadow-discrepancy', undefined, {
          sellerLeadUuid,
          discrepancies: {
            hasReferrals: { legacy: true, sellerTransaction: 'null' },
          },
        });
      }
      return;
    }
    const discrepancies: Record<string, unknown> = {};

    if (txReferrals.isPartnershipLead !== legacyIsPartnershipLead) {
      discrepancies.isPartnershipLead = {
        legacy: legacyIsPartnershipLead,
        sellerTransaction: txReferrals.isPartnershipLead,
      };
    }

    // txReferrals.blockBuyerContent reflects the backend's computation; the legacy value
    // additionally ORs in isAgentChannel (a channel-based check), so minor divergence is expected.
    if (txReferrals.blockBuyerContent !== blockBuyerContent) {
      discrepancies.blockBuyerContent = {
        legacy: blockBuyerContent,
        sellerTransaction: txReferrals.blockBuyerContent,
      };
    }

    // txReferrals.isAgent maps to the computed shouldSeeAgentExperience, which also
    // factors in lead.agentExperience, so minor divergence is possible.
    if (txReferrals.isAgent !== shouldSeeAgentExperience) {
      discrepancies.isAgent = {
        legacy: shouldSeeAgentExperience,
        sellerTransaction: txReferrals.isAgent,
      };
    }

    // Check flags
    const { referralFlags } = txReferrals;

    if (referralFlags.hasAgentReferral !== partnershipReferralData.hasAgentReferral) {
      discrepancies.hasAgentReferral = {
        legacy: partnershipReferralData.hasAgentReferral,
        sellerTransaction: referralFlags.hasAgentReferral,
      };
    }

    // isReferredByAgent = agent has REFERRAL attribution (legacy: isTrueAgentReferral)
    if (referralFlags.isReferredByAgent !== partnershipReferralData.isTrueAgentReferral) {
      discrepancies.isReferredByAgent = {
        legacy: partnershipReferralData.isTrueAgentReferral,
        sellerTransaction: referralFlags.isReferredByAgent,
      };
    }

    if (referralFlags.hasOnlineRealEstateReferral !== partnershipReferralData.hasOreReferral) {
      discrepancies.hasOnlineRealEstateReferral = {
        legacy: partnershipReferralData.hasOreReferral,
        sellerTransaction: referralFlags.hasOnlineRealEstateReferral,
      };
    }

    if (referralFlags.hasHomebuilderReferral !== partnershipReferralData.hasHomebuilderReferral) {
      discrepancies.hasHomebuilderReferral = {
        legacy: partnershipReferralData.hasHomebuilderReferral,
        sellerTransaction: referralFlags.hasHomebuilderReferral,
      };
    }

    if (referralFlags.hasMarkSpainReferral !== partnershipReferralData.hasMarkSpainReferral) {
      discrepancies.hasMarkSpainReferral = {
        legacy: partnershipReferralData.hasMarkSpainReferral,
        sellerTransaction: referralFlags.hasMarkSpainReferral,
      };
    }

    if (
      referralFlags.hasEasyStreetOffersReferral !==
      partnershipReferralData.hasEasyStreetOffersReferral
    ) {
      discrepancies.hasEasyStreetOffersReferral = {
        legacy: partnershipReferralData.hasEasyStreetOffersReferral,
        sellerTransaction: referralFlags.hasEasyStreetOffersReferral,
      };
    }

    // hasLennarTradeInReferral maps to legacy isLennarTradeIn
    if (referralFlags.hasLennarTradeInReferral !== partnershipReferralData.isLennarTradeIn) {
      discrepancies.hasLennarTradeInReferral = {
        legacy: partnershipReferralData.isLennarTradeIn,
        sellerTransaction: referralFlags.hasLennarTradeInReferral,
      };
    }

    if (Object.keys(discrepancies).length > 0) {
      trackError('partnership-referrals-shadow-discrepancy', undefined, {
        sellerLeadUuid,
        discrepancies,
      });
    }
  }, [
    sellerTransaction,
    sellerTransactionLoading,
    partnershipReferralData,
    blockBuyerContent,
    shouldSeeAgentExperience,
    sellerLeadUuid,
    trackError,
  ]);
};
